import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './ui/tokens.css';
import { ThemeProvider } from './ui/theme';

/**
 * A previously installed production PWA service worker can keep serving an
 * old app shell while Vite is running on the same localhost origin. In dev,
 * remove that stale worker and its caches before mounting React so local
 * testing always reflects the source files currently on disk.
 */
async function removeStaleDevelopmentWorker(): Promise<boolean> {
  if (!import.meta.env.DEV || !('serviceWorker' in navigator)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  if (!registrations.length) return false;

  await Promise.all(registrations.map(registration => registration.unregister()));
  if ('caches' in window) {
    const cacheNames = await window.caches.keys();
    await Promise.all(cacheNames.map(name => window.caches.delete(name)));
  }
  return true;
}

async function startApp() {
  try {
    const removedWorker = await removeStaleDevelopmentWorker();
    if (removedWorker) {
      // Reload once without the stale worker; the next load mounts normally.
      window.location.reload();
      return;
    }
  } catch (error) {
    // A browser may disallow service-worker management; don't prevent the app
    // from starting. Manual unregister is still available in DevTools.
    console.warn('Could not clear stale development service worker:', error);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </StrictMode>,
  );
}

void startApp();
